import logger from '@greenlight/logger';
import axios, { Method } from 'axios';

const delayMs = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const axiosInstance = axios.create();

export interface HttpRequestConfig<ResponseData> {
  method: Method;
  url: string;
  /**
   * Should throw an error if response data is invalid.
   * Use runtype's .check function.
   */
  validateResponse: (response: unknown) => ResponseData;
  headers?: Record<string, unknown>;
  data?: Record<string, unknown>;
  params?: { [key: string]: unknown };
  retries?: number;
  backoffMs?: number;
}

export interface HttpRequestResponse<ResponseData> {
  request: any;
  data: ResponseData;
  status: number;
  statusText: string;
  headers: unknown;
}

export async function sendHttpRequest<ResponseData>({
  validateResponse,
  retries = 0,
  backoffMs = 1000,
  ...requestConfig
}: HttpRequestConfig<ResponseData>): Promise<
  HttpRequestResponse<ResponseData>
> {
  let result;
  try {
    result = await axiosInstance(requestConfig).then(response => {
      try {
        return {
          ...response,
          data: validateResponse(response.data)
        };
      } catch (error) {
        logger.error(error);
        throw error;
      }
    });
  } catch (error) {
    if (retries > 0) {
      await delayMs(backoffMs);
      result = await sendHttpRequest({
        ...requestConfig,
        validateResponse,
        retries: retries - 1,
        backoffMs: backoffMs * 2
      });
    } else {
      throw error;
    }
  }
  return result;
}
